/**
 * SoulCheatGuide — how-to for the Soul Sheets tab.
 *
 * Audience: Dipali, Nisha, Tanisha, Shiva, Nikita, Samar.  Anyone who lands
 * on /client-intelligence/{client} → Soul tab.
 *
 * Goals:
 *   - Stop people accidentally regenerating profiles and losing edits
 *   - Explain what triggers a Soul Sheet vs. when to manually generate
 *   - Make the "Push to Zoho" button safe to use
 */
import React from 'react';
import {
  Sparkles, Eye, Search, Download, Send, RefreshCw, Mail, Lock,
  HelpCircle,
} from 'lucide-react';
import CheatGuideShell, {
  Section, Btn, Pill, Warn, Note, Choice,
} from './CheatGuideShell';

export default function SoulCheatGuide() {
  return (
    <CheatGuideShell
      title="Cheat Guide — what is a Soul Sheet & how do I use it?"
      storageKey="sb_soul_cheat_open"
      testId="soul-cheat-guide">

      <Section title="What is a Soul Sheet?" icon={<Sparkles size={13} />}>
        A one-page narrative profile of a member — the Concierge® team's
        memory of who they are: how they like to dine, where they travel, the
        non-negotiables, the little tells.{' '}
        <strong>Built from their Soul Form responses + Zoho service history.</strong>
        {' '}Used by LSMs to personalise every conversation, ticket reply, and
        newsletter for that member.
      </Section>

      <Section title="The two kinds of rows on this tab" icon={<Eye size={13} />}>
        <Choice label="Soul Triggers (top section)" recommended>
          Members where SOMETHING has changed — Zoho ticket closed, Soul Form
          completed, anniversary approaching. <strong>This is your daily action list.</strong>
          {' '}Click any trigger row → Soul Sheet drawer opens.
        </Choice>
        <Choice label="All Members table (below)">
          Full searchable list — filter by tier, city, "only Soul Form
          completed". Click any member row → Soul Sheet drawer.
        </Choice>
      </Section>

      <Section title="Inside the Soul Sheet drawer" icon={<Sparkles size={13} />}>
        <ul className="space-y-1.5 list-disc ml-5">
          <li><strong>Member name + tier + email</strong> at the top</li>
          <li><strong>Soul Sheet narrative</strong> — the AI-written profile</li>
          <li><strong>Concierge Notes</strong> — actionable recommendations based on Soul Form + recent tickets</li>
          <li><strong>Service History</strong> — chronological list of Zoho Desk tickets (multi-bridge: matches by email, phone, name, member#)</li>
          <li><Btn icon={<Sparkles size={11} />} label="Generate" /> button — regenerates the narrative from scratch with latest data</li>
          <li><Btn icon={<Send size={11} />} label="Push to Zoho" /> button — saves the latest version as a CRM note on the member's record</li>
        </ul>
      </Section>

      <Section title="When SHOULD you regenerate?" icon={<RefreshCw size={13} />}>
        <ul className="space-y-1 list-disc ml-5">
          <li>After a NEW Soul Form has just been submitted</li>
          <li>After a meaningful ticket activity (anniversary planning, complaint, big trip)</li>
          <li>If the existing sheet is older than ~3 months and the member has been active</li>
        </ul>
        <Warn>
          <strong>Don't</strong> regenerate just to "see what it does" — Claude rewrites
          from scratch each time.  If a teammate has hand-edited a sheet, your
          regenerate will overwrite their work.  Read first; regenerate with intent.
        </Warn>
      </Section>

      <Section title="Sending a Soul Form invite" icon={<Mail size={13} />}>
        Two ways:
        <ol className="space-y-1 list-decimal ml-5 mt-2">
          <li>
            <strong>One member at a time</strong> — open their Soul Sheet drawer →
            <Btn icon={<Mail size={11} />} label="Send Soul Form" />.
          </li>
          <li>
            <strong>Bulk</strong> — go to the Members tab →
            <Btn icon={<Send size={11} />} label="Bulk invite" /> button.
            Send to all members without a completed Soul Form, throttled per day
            so we don't blast inboxes.
          </li>
        </ol>
      </Section>

      <Section title="Soul Form short-link" icon={<Sparkles size={13} />}>
        Each member gets a unique URL like <code>scaleboard.in/r/abc123</code>{' '}
        with a 30-day expiry. Mobile-optimised. Pre-fills name + email.
        Once submitted → a Soul Trigger lights up here within 60 seconds.
      </Section>

      <Section title="Permissions" icon={<Lock size={13} />}>
        <ul className="space-y-1 list-disc ml-5">
          <li><Pill bg="#DBEAFE" fg="#1E3A8A">CEO</Pill> — full access</li>
          <li><Pill bg="#DCFCE7" fg="#166534">Leader / Sub-leader</Pill> — read + send invites + push to Zoho</li>
          <li><Pill bg="#FEF3C7" fg="#92400E">Read-only roles</Pill> — drawer opens but Generate/Push are disabled</li>
        </ul>
      </Section>

      <Section title="Recent updates · May 2026" icon={<RefreshCw size={13} />}>
        <ul className="space-y-1 list-disc ml-5 text-[13px]">
          <li><strong>1,237 canonical JLR members</strong> · 305 Soul Forms completed (≈25%).</li>
          <li><strong>Aggregate Soul slide</strong> — slide 25.5 in the Member Experience Deck rolls up the most-loved cuisines, dream restaurants, escape types and ideal celebrations across the whole member base. Shows JLR what their members collectively crave.</li>
          <li><strong>Per-member Soul Sheets</strong> read live from <code>zoho_contacts.soul_form</code> — no caching layer between Zoho and the dashboard.</li>
        </ul>
      </Section>

      <Note color="#FFFFFFAA">
        <strong style={{ color: '#7A5C2B' }}>Pro tip:</strong> Before any big member
        interaction (call, email, meeting), open their Soul Sheet first. The
        Concierge Notes will give you 3-5 "something to mention" angles that
        immediately make the conversation feel personal.
      </Note>
    </CheatGuideShell>
  );
}
